import { CustomerService } from "@/src/services/merchant/customer-service"
import { jwtDecode } from "jwt-decode"
import { signIn } from "next-auth/react"

export interface CreateCustomerProps {
    merchantId: string | undefined
    email: string
    username: string
    password: string
    phone: string
    appAccessToken: string | undefined
}

interface CreateCustomerResponse {
    customer: Customer.Response
    auth: Customer.AuthResponse
}

export interface AuthCustomerProps {
    merchantId: string | undefined
    email: string
    password: string
    appAccessToken: string | undefined
    otpCode?: string
}

interface AuthCustomerResponse {
    customer: Customer.Response
    auth: Customer.AuthResponse
}

export interface SignInProps {
    authResponse?: Customer.AuthResponse
    merchantId?: string
    email: string
    password?: string
    isOtp?: boolean
}

export class LoginFunctions {
    public static async customerExists(merchantId: string | undefined, email: string, appAccessToken: string | undefined) {
        if (!merchantId)
            throw Error('Não foi possível obter o código do cardápio.')

        if (!appAccessToken)
            throw Error('Não foi possível obter o app config.')

        const response = await CustomerService.exists(appAccessToken, merchantId, email)

        if (!response.success)
            throw Error(response.errorMessage)

        return response.model
    }

    public static async getCustomer(authResponse: Customer.AuthResponse, appAccessToken?: string) {
        const jwt = authResponse.accessToken

        const decoded = jwtDecode(jwt)

        if (!decoded?.sub)
            throw Error("Não foi possível obter o código do usuário.")

        const customerResponse = await CustomerService.getCustomer(decoded.sub, authResponse.accessToken, appAccessToken)

        if (!customerResponse.success || !customerResponse.model)
            throw Error(customerResponse.errorMessage)

        return customerResponse.model
    }

    public static async authCustomer(model: AuthCustomerProps) {
        if (!model.merchantId)
            throw Error('Não foi possível obter o código do cardápio.')

        const request: Customer.AuthRequest = {
            email: model.email,
            password: model.password,
            merchantId: model.merchantId
        }

        const authResponse = await CustomerService.authCustomer(request, model.appAccessToken)

        if (!authResponse.success || !authResponse.model?.accessToken)
            throw Error(authResponse.errorMessage)

        const customerModel = await this.getCustomer(authResponse.model, model.appAccessToken)

        const customResponse: AuthCustomerResponse = {
            customer: customerModel,
            auth: authResponse.model
        }

        return customResponse
    }

    public static async authCustomerWithOtp(model: AuthCustomerProps) {
        if (!model.merchantId)
            throw Error('Não foi possível obter o código do cardápio.')

        if (!model.otpCode)
            throw Error('O código OTP não pode se vazio.')

        const request: Customer.OtpRequest = {
            merchantId: model.merchantId,
            email: model.email,
            otp: model.otpCode
        }

        const otpResponse = await CustomerService.OtpAuth(request, model.appAccessToken)

        if (otpResponse.status === 400)
            throw Error(`O código ${request.otp} não é válido.`)

        if (!otpResponse.success || !otpResponse.model)
            throw (otpResponse.errorMessage)

        const customerModel = await this.getCustomer(otpResponse.model, model.appAccessToken)

        const customResponse: AuthCustomerResponse = {
            customer: customerModel,
            auth: otpResponse.model
        }

        return customResponse
    }

    public static async signInCustomer(props: SignInProps) {
        if (!props.merchantId || !props.authResponse)
            throw Error('Falha ao realizar login.')

        const accessToken = props.authResponse.accessToken
        const refreshToken = props.authResponse.refreshToken
        const accessTokenExpiresIn = props.authResponse.accessTokenExpiresIn

        let password = props.password

        if (props.isOtp)
            password = Date.now().toString()

        const merchantId = props.merchantId
        const email = props.email

        const result = await signIn('credentials', {
            redirect: false,
            email,
            password,
            merchantId,
            accessToken,
            refreshToken,
            accessTokenExpiresIn
        })

        if (result?.error) {
            throw Error(`(${result.status}) ${result.error}`)
        }
    }
}